import { CaretDown } from "@phosphor-icons/react";
import {
  FormGroup,
  Select,
  SelectItem,
  SelectPopover,
  SelectProvider,
  SelectRow,
  parseCoverImageUrl,
} from "@replicate/ui";
import { match } from "ts-pattern";
import type { Model } from "../types";
import { route } from "../urls";
import { LogoGlyph } from "./logo-glyph";
import VideoCoverImage from "./video-cover-image";

export default function AuthorModelsDropdown({
  currentModel,
  models,
}: {
  currentModel: Model;
  models: Model[];
}) {
  if (models.length <= 1) return null;

  return (
    <ModelPicker
      selectedModel={currentModel}
      models={[currentModel, ...models]}
    />
  );
}

function ModelPicker({
  selectedModel,
  models,
}: { selectedModel: Model; models: Model[] }) {
  return (
    <div className="w-full flex items-center gap-4">
      <div className="flex-1">
        <SelectProvider orientation="both" value={selectedModel.name}>
          <FormGroup size="sm" id="status">
            <div className="[&_*]:ring-transparent [&>*]:hover:bg-r8-gray-3 [&_*]:p-[0.25px] [&_*]:relative whitespace-nowrap">
              <FormGroup.Control
                endAdornment={<CaretDown />}
                endAdornmentOptions={{
                  inert: true,
                  absolute: true,
                }}
                input={<Select id="status">More by this user</Select>}
              />
            </div>
          </FormGroup>
          <SelectPopover role="grid" portal gutter={8} className="z-0">
            <div className="p-2 relative font-semibold bg-r8-gray-3 text-r8-sm flex justify-between">
              <div>Select a model</div>
              <a
                className="link"
                href={route("account_detail", {
                  username: selectedModel.owner,
                })}
              >
                View profile
              </a>
            </div>
            <div className="max-h-[calc(100vh-380px)] overflow-scroll border-y border-r8-gray-5 flex flex-col divide-y divide-r8-gray-4 bg-r8-gray-1">
              {models.map((model, i) => (
                <SelectRow key={`chunk-${i}`}>
                  <ModelSelectItem model={model} key={model.name} />
                </SelectRow>
              ))}
            </div>
          </SelectPopover>
        </SelectProvider>
      </div>
    </div>
  );
}

function ModelSelectItem({ model }: { model: Model }) {
  const coverImage = parseCoverImageUrl({ url: model.cover_image_url });
  return (
    <SelectItem
      role="gridcell"
      className="group max-w-[90vw] cursor-pointer h-fit flex items-center gap-4 hover:bg-r8-gray-2 p-2"
      value={model.name}
      render={
        // biome-ignore lint/a11y/useAnchorContent: handled by ariakit
        <a
          className="no-default"
          href={route("model_detail", {
            username: model.owner,
            name: model.name,
          })}
        />
      }
    >
      <div className='overflow-hidden size-12 flex-shrink-0 aspect-square bg-r8-gray-12 relative group-data-[active-item="true"]:ring-4 ring-r8-gray-12 group-data-[active-item="true"]:shadow-xl group-data-[active-item="true"]:shadow-black/5 dark:group-data-[active-item="true"]:shadow-white/15'>
        {coverImage ? (
          <div className="isolate relative w-full h-full block">
            {match(coverImage.fileType)
              .with("image", () => (
                <img
                  className="object-cover h-full w-full"
                  src={coverImage.url}
                  alt={model.name}
                />
              ))
              .with("video", () => <VideoCoverImage url={coverImage.url} />)
              .exhaustive()}
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <LogoGlyph />
          </div>
        )}
      </div>
      <div className="overflow-hidden space-y-1 pr-4">
        <div className="flex text-r8-sm items-center">
          <span>{model.owner}</span>
          <span className="px-1">/</span>
          <span className="group-data-[active-item='true']:underline decoration-r8-gray-10 truncate font-semibold">
            {model.name}
          </span>
        </div>
        <p className="text-r8-xs text-r8-gray-10 truncate w-full max-w-lg">
          {model.description}
        </p>
      </div>
    </SelectItem>
  );
}
